import { CALL_DIRECTION_LABELS, LEAD_SOURCE_LABELS, PROPERTY_DEAL_TYPE_LABELS } from '../common/labels'
import { EVENT_TYPE_LABELS } from '../api/event.types'
import { fromLocalParts, toLocalParts } from './recurrence'
import type { UserRole } from './user.types'

/**
 * KPI Reportov: katalóg metrík a práca s mesiacmi
 * (spec docs/superpowers/specs/2026-10-20-reports-kpi-design.md).
 *
 * Metadáta zdieľa worker (kľúče), API (validácia `metric`) aj admin (popisy, formát). SQL
 * definície sú len vo workeri (`apps/worker/src/reports/report-kpi.metrics.ts`). Typ
 * `Record<ReportMetricKey, …>` tam vynúti práve jednu definíciu pre každý kľúč.
 */

export type ReportMetricGroup = 'activity' | 'results'
export type ReportValueFormat = 'count' | 'currency' | 'hours' | 'seconds' | 'days' | 'rating' | 'percent'
/** Zoskupenie riadkov tabuľky (query `groupBy`); jediný zdroj pre API validáciu aj admin. */
export const REPORT_GROUP_BYS = ['agent', 'branch', 'tenant'] as const
export type ReportGroupBy = (typeof REPORT_GROUP_BYS)[number]

export interface ReportMetricMeta {
  label: string
  shortLabel: string
  group: ReportMetricGroup
  format: ReportValueFormat
  /** Ukladá sa rozpadnutá podľa `dim`; hodnota metriky = súčet cez dim. */
  hasDim: boolean
  /** Pomocná metrika (čitateľ alebo menovateľ odvodeného stĺpca), v tabuľke nie je stĺpcom. */
  hidden?: boolean
}

export const REPORT_METRICS = {
  // Aktivita
  EVENTS_CREATED: { label: 'Vytvorené udalosti', shortLabel: 'Udalosti', group: 'activity', format: 'count', hasDim: true },
  TASKS_CREATED: { label: 'Vytvorené úlohy', shortLabel: 'Úlohy', group: 'activity', format: 'count', hasDim: false },
  TASKS_COMPLETED: { label: 'Splnené úlohy', shortLabel: 'Splnené', group: 'activity', format: 'count', hasDim: false },
  TASKS_COMPLETED_LATE: { label: 'Splnené úlohy po termíne', shortLabel: 'Po termíne', group: 'activity', format: 'count', hasDim: false },
  INVITES_SENT: { label: 'Odoslané pozvánky', shortLabel: 'Pozvánky', group: 'activity', format: 'count', hasDim: false },
  INVITES_ACCEPTED: { label: 'Prijaté pozvánky', shortLabel: 'Prijaté pozv.', group: 'activity', format: 'count', hasDim: false },
  CALLS: { label: 'Hovory', shortLabel: 'Hovory', group: 'activity', format: 'count', hasDim: true },
  CALLS_CONNECTED: { label: 'Uskutočnené hovory', shortLabel: 'Uskutočnené', group: 'activity', format: 'count', hasDim: false, hidden: true },
  CALL_SECONDS: { label: 'Čas hovorov', shortLabel: 'Čas hovorov', group: 'activity', format: 'seconds', hasDim: false },
  CONTACTS_CREATED: { label: 'Nové kontakty', shortLabel: 'Kontakty', group: 'activity', format: 'count', hasDim: true },
  CONTACTS_FIRST_CONTACTED: { label: 'Prvé kontaktovanie', shortLabel: 'Kontaktované', group: 'activity', format: 'count', hasDim: false },
  CONTACT_RESPONSE_COUNT: { label: 'Kontakty s meraným časom reakcie', shortLabel: 'Reakcie', group: 'activity', format: 'count', hasDim: false, hidden: true },
  CONTACT_RESPONSE_HOURS_SUM: { label: 'Súčet hodín do reakcie', shortLabel: 'Hodiny reakcie', group: 'activity', format: 'hours', hasDim: false, hidden: true },
  NOTES_CREATED: { label: 'Poznámky', shortLabel: 'Poznámky', group: 'activity', format: 'count', hasDim: false },
  EMAILS_SENT: { label: 'Odoslané e-maily', shortLabel: 'Odosl. e-maily', group: 'activity', format: 'count', hasDim: false },
  EMAILS_RECEIVED: { label: 'Prijaté e-maily', shortLabel: 'Prij. e-maily', group: 'activity', format: 'count', hasDim: false },
  AI_REQUESTS: { label: 'Dotazy na AI asistenta', shortLabel: 'AI dotazy', group: 'activity', format: 'count', hasDim: false },
  // Výsledky
  DEALS_CREATED: { label: 'Nové obchody', shortLabel: 'Nové obchody', group: 'results', format: 'count', hasDim: false },
  DEALS_WON: { label: 'Uzavreté obchody', shortLabel: 'Uzavreté', group: 'results', format: 'count', hasDim: false },
  DEALS_WON_VALUE: { label: 'Hodnota uzavretých obchodov', shortLabel: 'Hodnota', group: 'results', format: 'currency', hasDim: false },
  DEAL_CLOSE_DAYS_SUM: { label: 'Súčet dní do uzavretia', shortLabel: 'Dni', group: 'results', format: 'days', hasDim: false, hidden: true },
  DEALS_LOST: { label: 'Stratené obchody', shortLabel: 'Stratené', group: 'results', format: 'count', hasDim: false },
  VIEWINGS_SCHEDULED: { label: 'Naplánované obhliadky', shortLabel: 'Napl. obhliadky', group: 'results', format: 'count', hasDim: false },
  VIEWINGS_COMPLETED: { label: 'Vykonané obhliadky', shortLabel: 'Vyk. obhliadky', group: 'results', format: 'count', hasDim: false },
  VIEWINGS_CANCELLED: { label: 'Zrušené obhliadky', shortLabel: 'Zruš. obhliadky', group: 'results', format: 'count', hasDim: false },
  VIEWINGS_RATED: { label: 'Hodnotené obhliadky', shortLabel: 'Hodnotené', group: 'results', format: 'count', hasDim: false, hidden: true },
  VIEWING_RATING_SUM: { label: 'Súčet hodnotení obhliadok', shortLabel: 'Hodnotenia', group: 'results', format: 'count', hasDim: false, hidden: true },
  PROPERTIES_PUBLISHED: { label: 'Publikované nehnuteľnosti', shortLabel: 'Publikované', group: 'results', format: 'count', hasDim: true },
  PROPERTIES_PUBLISHED_EXCLUSIVE: { label: 'Publikované s výhradnou zmluvou', shortLabel: 'Výhradné', group: 'results', format: 'count', hasDim: false, hidden: true },
  PROPERTIES_SOLD: { label: 'Predané nehnuteľnosti', shortLabel: 'Predané', group: 'results', format: 'count', hasDim: false },
  PROPERTIES_RENTED: { label: 'Prenajaté nehnuteľnosti', shortLabel: 'Prenajaté', group: 'results', format: 'count', hasDim: false },
  CLIENTS_CREATED: { label: 'Noví zákazníci', shortLabel: 'Zákazníci', group: 'results', format: 'count', hasDim: false },
} as const satisfies Record<string, ReportMetricMeta>

export type ReportMetricKey = keyof typeof REPORT_METRICS
export const REPORT_METRIC_KEYS = Object.keys(REPORT_METRICS) as ReportMetricKey[]
/** Ten istý katalóg so širokým typom (prístup k voliteľnému `hidden`). */
export const REPORT_METRIC_META: Record<ReportMetricKey, ReportMetricMeta> = REPORT_METRICS

export interface ReportDerivedMeta {
  label: string
  shortLabel: string
  group: ReportMetricGroup
  format: ReportValueFormat
  /** Hodnota = súčet čitateľa / súčet menovateľa; percentá × 100. */
  numerator: readonly ReportMetricKey[]
  denominator: readonly ReportMetricKey[]
}

export const REPORT_DERIVED = {
  TASKS_LATE_RATE: { label: 'Podiel úloh splnených po termíne', shortLabel: '% po termíne', group: 'activity', format: 'percent', numerator: ['TASKS_COMPLETED_LATE'], denominator: ['TASKS_COMPLETED'] },
  INVITES_ACCEPT_RATE: { label: 'Podiel prijatých pozvánok', shortLabel: '% prijatých', group: 'activity', format: 'percent', numerator: ['INVITES_ACCEPTED'], denominator: ['INVITES_SENT'] },
  CALL_AVG_SECONDS: { label: 'Priemerná dĺžka hovoru', shortLabel: 'Ø hovor', group: 'activity', format: 'seconds', numerator: ['CALL_SECONDS'], denominator: ['CALLS_CONNECTED'] },
  RESPONSE_AVG_HOURS: { label: 'Priemerný čas reakcie na kontakt', shortLabel: 'Ø reakcia', group: 'activity', format: 'hours', numerator: ['CONTACT_RESPONSE_HOURS_SUM'], denominator: ['CONTACT_RESPONSE_COUNT'] },
  DEALS_WIN_RATE: { label: 'Úspešnosť obchodov', shortLabel: 'Úspešnosť', group: 'results', format: 'percent', numerator: ['DEALS_WON'], denominator: ['DEALS_WON', 'DEALS_LOST'] },
  DEAL_AVG_DAYS: { label: 'Priemerná dĺžka obchodu', shortLabel: 'Ø dĺžka', group: 'results', format: 'days', numerator: ['DEAL_CLOSE_DAYS_SUM'], denominator: ['DEALS_WON'] },
  VIEWING_AVG_RATING: { label: 'Priemerné hodnotenie obhliadok', shortLabel: 'Rating', group: 'results', format: 'rating', numerator: ['VIEWING_RATING_SUM'], denominator: ['VIEWINGS_RATED'] },
  EXCLUSIVE_SHARE: { label: 'Podiel výhradných zmlúv', shortLabel: '% výhradných', group: 'results', format: 'percent', numerator: ['PROPERTIES_PUBLISHED_EXCLUSIVE'], denominator: ['PROPERTIES_PUBLISHED'] },
} as const satisfies Record<string, ReportDerivedMeta>

export type ReportDerivedKey = keyof typeof REPORT_DERIVED
export const REPORT_DERIVED_META: Record<ReportDerivedKey, ReportDerivedMeta> = REPORT_DERIVED
export type ReportColumnKey = ReportMetricKey | ReportDerivedKey

/** Poradie stĺpcov tabuliek Aktivita a Výsledky; odvodený stĺpec stojí hneď za svojimi metrikami. */
export const REPORT_COLUMN_ORDER: Record<ReportMetricGroup, readonly ReportColumnKey[]> = {
  activity: [
    'EVENTS_CREATED', 'TASKS_CREATED', 'TASKS_COMPLETED', 'TASKS_COMPLETED_LATE', 'TASKS_LATE_RATE',
    'INVITES_SENT', 'INVITES_ACCEPTED', 'INVITES_ACCEPT_RATE', 'CALLS', 'CALL_SECONDS', 'CALL_AVG_SECONDS',
    'CONTACTS_CREATED', 'CONTACTS_FIRST_CONTACTED', 'RESPONSE_AVG_HOURS', 'NOTES_CREATED',
    'EMAILS_SENT', 'EMAILS_RECEIVED', 'AI_REQUESTS',
  ],
  results: [
    'DEALS_CREATED', 'DEALS_WON', 'DEALS_WON_VALUE', 'DEALS_LOST', 'DEALS_WIN_RATE', 'DEAL_AVG_DAYS',
    'VIEWINGS_SCHEDULED', 'VIEWINGS_COMPLETED', 'VIEWINGS_CANCELLED', 'VIEWING_AVG_RATING',
    'PROPERTIES_PUBLISHED', 'EXCLUSIVE_SHARE', 'PROPERTIES_SOLD', 'PROPERTIES_RENTED', 'CLIENTS_CREATED',
  ],
}

/** Popisy hodnôt `dim` pre metriky s rozpadom. */
export const REPORT_DIM_LABELS: Partial<Record<ReportMetricKey, Record<string, string>>> = {
  EVENTS_CREATED: EVENT_TYPE_LABELS,
  CALLS: CALL_DIRECTION_LABELS,
  CONTACTS_CREATED: LEAD_SOURCE_LABELS,
  PROPERTIES_PUBLISHED: PROPERTY_DEAL_TYPE_LABELS,
}

/** Roly s vlastným riadkom v tabuľke Reportov; hodnoty ostatných idú do riadku „Ostatní“. */
export const REPORT_ROW_ROLES = ['BROKER', 'BRANCH_MANAGER', 'OWNER', 'ASSISTANT'] as const satisfies readonly UserRole[]
export const REPORT_MAX_MONTHS = 36
export const REPORT_MAX_TREND_IDS = 5
/** Id riadku „Ostatní“ (groupBy=agent). */
export const REPORT_OTHER_ID = 'other'
/** Id riadku „Bez pobočky“ (groupBy=branch). */
export const REPORT_NO_BRANCH_ID = 'none'
/** Id série grafu za celý scope (trend bez `ids`). */
export const REPORT_TOTAL_ID = 'total'

const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key)

export function isReportMetricKey(key: string): key is ReportMetricKey {
  return hasOwn(REPORT_METRICS, key)
}

export function isReportDerivedKey(key: string): key is ReportDerivedKey {
  return hasOwn(REPORT_DERIVED, key)
}

/** Popis a formát stĺpca tabuľky (metrika aj odvodený stĺpec). */
export function reportColumnMeta(key: ReportColumnKey): Omit<ReportMetricMeta, 'hidden'> {
  if (isReportDerivedKey(key)) {
    const d = REPORT_DERIVED_META[key]
    return { label: d.label, shortLabel: d.shortLabel, group: d.group, format: d.format, hasDim: false }
  }
  const m = REPORT_METRIC_META[key]
  return { label: m.label, shortLabel: m.shortLabel, group: m.group, format: m.format, hasDim: m.hasDim }
}

/** Metriky, z ktorých sa stĺpec počíta (metrika = ona sama). */
export function reportBaseMetrics(key: ReportColumnKey): ReportMetricKey[] {
  if (!isReportDerivedKey(key)) return [key]
  const d = REPORT_DERIVED_META[key]
  return [...new Set([...d.numerator, ...d.denominator])]
}

/** Hodnota stĺpca zo súčtov metrík. Odvodený stĺpec s nulovým menovateľom = null. */
export function reportColumnValue(key: ReportColumnKey, metrics: Partial<Record<string, number>>): number | null {
  if (!isReportDerivedKey(key)) return metrics[key] ?? 0
  const d = REPORT_DERIVED_META[key]
  const sum = (keys: readonly ReportMetricKey[]) => keys.reduce((s, k) => s + (metrics[k] ?? 0), 0)
  const denominator = sum(d.denominator)
  if (denominator === 0) return null
  const ratio = sum(d.numerator) / denominator
  return d.format === 'percent' ? ratio * 100 : ratio
}

/** Mesačný priebeh stĺpca zo sérií základných metrík (rovnako dlhé polia). */
export function reportColumnSeries(
  key: ReportColumnKey,
  byMetric: Partial<Record<ReportMetricKey, readonly number[]>>,
  length: number,
): Array<number | null> {
  return Array.from({ length }, (_, i) => {
    const month: Partial<Record<string, number>> = {}
    for (const [metric, values] of Object.entries(byMetric)) month[metric] = values?.[i] ?? 0
    return reportColumnValue(key, month)
  })
}

// ---------- mesiace (Europe/Bratislava) ----------

export const REPORT_TIME_ZONE = 'Europe/Bratislava'
export const REPORT_PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/

export function isReportPeriod(value: string): boolean {
  return REPORT_PERIOD_RE.test(value)
}

/** Mesiac YYYY-MM, do ktorého okamih patrí v Europe/Bratislava. */
export function currentReportPeriod(now: Date = new Date()): string {
  const { y, m } = toLocalParts(now, REPORT_TIME_ZONE)
  return `${y}-${String(m).padStart(2, '0')}`
}

/** Rok a mesiac (1–12) z `YYYY-MM`; neplatné obdobie vyhodí RangeError (nie potichu NaN). */
function parsePeriod(period: string): [number, number] {
  if (!isReportPeriod(period)) throw new RangeError(`Neplatné obdobie: ${period}`)
  const [y, m] = period.split('-').map(Number)
  return [y!, m!]
}

export function addMonths(period: string, delta: number): string {
  const [y, m] = parsePeriod(period)
  const index = y * 12 + (m - 1) + delta
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`
}

/** Počet mesiacov vrátane oboch krajov; `from` po `to` = 0. */
export function monthCount(from: string, to: string): number {
  const [fy, fm] = parsePeriod(from)
  const [ty, tm] = parsePeriod(to)
  return Math.max(0, ty * 12 + tm - (fy * 12 + fm) + 1)
}

export function monthsInRange(from: string, to: string): string[] {
  return Array.from({ length: monthCount(from, to) }, (_, i) => addMonths(from, i))
}

/** Rovnako dlhé obdobie tesne pred [from, to]. */
export function previousRange(from: string, to: string): { from: string; to: string } {
  const n = monthCount(from, to)
  return { from: addMonths(from, -n), to: addMonths(from, -1) }
}

/** Prvý okamih mesiaca v Europe/Bratislava ako UTC `Date` (letný čas rieši `fromLocalParts`). */
export function monthStartUtc(period: string): Date {
  const [y, m] = parsePeriod(period)
  return fromLocalParts({ y, m, d: 1, hh: 0, mm: 0, ss: 0 }, REPORT_TIME_ZONE)
}
